import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { injectIntl, intlShape } from 'react-intl';
import { withUser, withModalActions, withGameActions } from 'core/hocs';
import { FormattedTag } from 'core/components';
import { USER_FIELDS } from 'core/constants';
import { isEmptyOrNil, getIntlKeyByEnv } from 'core/helpers';
import { IconArrowDown } from 'components/icons/icon-arrow-down/icon-arrow-down';
import { DottedLineXs } from 'components/icons/loyalty-dotted-lines/dotted-line-xs';
import colors from 'customizations/js/color-variables';
import { IconArrowBottomBoldWithLine } from './icon-arrow-bottom-bold-with-line';
import { MODAL_IDS } from '../../../constants';

import './loyalty-card.scss';
import './loyalty-card-dotted-lines.scss';

class LoyaltyCardUI extends Component {
  static propTypes = {
    name: PropTypes.string.isRequired,
    // characterId: PropTypes.number,
    order: PropTypes.number.isRequired,
    pointsFrom: PropTypes.number.isRequired,
    alignClassName: PropTypes.string.isRequired,
    className: PropTypes.string.isRequired,
    imgUrl: PropTypes.string.isRequired,
    isLevelAccomplished: PropTypes.bool.isRequired,
    gift: PropTypes.number,
    freespins: PropTypes.shape().isRequired,
    dottedLineIcon: PropTypes.arrayOf(PropTypes.func),
    isActive: PropTypes.bool.isRequired,
    dottedLineWrapperClassName: PropTypes.string.isRequired,
    currencySymbol: PropTypes.string,
    // eslint-disable-next-line react/no-unused-prop-types
    intl: intlShape.isRequired,
  };

  static defaultProps = {
    // characterId: 1,
    gift: '',
    dottedLineIcon: null,
    currencySymbol: '',
  };

  state = {
    // eslint-disable-next-line react/no-unused-state
    isClickedOnGiftGame: false,
  };

  static getDerivedStateFromProps(props, { isClickedOnGiftGame }) {
    const {
      isUserLoggedIn, isUserRegistered,
      isSignUpCompleted, openModal,
      openGame,
      freespins: { game },
      intl: { formatMessage },
    } = props;

    if (isClickedOnGiftGame && isUserLoggedIn && isSignUpCompleted && !isEmptyOrNil(game)) {
      const gameId = formatMessage({ id: getIntlKeyByEnv(game.gameId) });
      const producer = formatMessage({ id: getIntlKeyByEnv(game.gameProducer) });

      openGame({ gameId, producer });

      return null;
    }

    let modalId;

    if (isClickedOnGiftGame) {
      if (!isUserRegistered && !isUserLoggedIn) {
        modalId = MODAL_IDS.SIGN_UP;
      } else if (isUserRegistered && !isUserLoggedIn) {
        modalId = MODAL_IDS.SIGN_IN;
      } else if (isUserLoggedIn && !isSignUpCompleted) {
        modalId = MODAL_IDS.COMPLETE_SIGN_UP;
      }

      if (modalId) {
        openModal(modalId);
      }
    }

    return { isClickedOnGiftGame: false };
  }

  // eslint-disable-next-line react/no-unused-state
  onGiftGameClick = () => this.setState({ isClickedOnGiftGame: true });

  getClassNameDottedLine = (order, isLevelAccomplished) => {
    let classNameIconDottedLineSm = '';
    let classNameIconDottedLineLg = '';

    if (isLevelAccomplished) {
      if (order === 2) {
        classNameIconDottedLineSm = 'dotted-line-sm-level-3-active';
      }

      if (order === 4) {
        classNameIconDottedLineSm = 'dotted-line-sm-level-5-active';
        classNameIconDottedLineLg = 'dotted-line-lg-level-5-active';
      }
    }

    return {
      classNameIconDottedLineSm,
      classNameIconDottedLineLg,
    };
  };

  render() {
    const {
      name,
      pointsFrom,
      alignClassName,
      className,
      dottedLineIcon,
      order,
      isActive,
      gift,
      currencySymbol,
      freespins,
      isLevelAccomplished,
      imgUrl,
      // characterId,
      dottedLineWrapperClassName,
    } = this.props;

    const isCardFirst = order === 1;
    const isCardLast = order === 5;
    const isLastCardActive = isActive && isCardLast;
    const points = pointsFrom === 0 ? pointsFrom : pointsFrom - 1;
    const iconImg = imgUrl ?? `/assets/public/images/loyalty-levels/${order}.png`;

    return (
      <>
        <div
          className={classNames(
            'position-relative loyalty-card  px-3 px-md-4',
            { 'last-active': isLastCardActive },
            { 'loyalty-card-1-active': isActive && isCardFirst },
            { 'bg-green-gradient': isActive },
            { 'bg-white': !isActive },
            alignClassName,
            className
          )}
        >
          <div
            style={{ backgroundImage: `url("${iconImg}")` }}
            className={classNames('loyalty-card-img position-absolute', `loyalty-card-img-${order}`, {
              'loyalty-card-img-disabled': !(isActive || isLevelAccomplished),
            })}
          />

          <FormattedTag
            defaultMessage={`Level ${order}`}
            id={name}
            className={classNames('h3 level-name font-family-oswald d-block mt-3_25 mt-md-4_25 mb-0_5 mb-md-1_25 letter-spacing-extra', {
              'text-white': isActive,
              'text-blue-dark': !isActive
            })}
          />
          <FormattedTag
            id="loyalty-program.points"
            values={{ value: points }}
            className={classNames('level-points border-bottom border-gray-10-20 pb-3 pb-md-3_75 d-block h4 font-weight-semi-bold letter-spacing-default', {
              'text-white-70': isActive,
              'text-blue-dark-75': !isActive,
            })}
          />

          <div className="d-flex mt-4_25 mt-md-4_5">
            {!isEmptyOrNil(freespins) && R.gt(R.prop('amount', freespins), 0) && (
              <div className="mr-7">
                <span
                  className={classNames('gift-amount font-weight-bold text-blue-dark', {
                    'text-white': isActive,
                    'text-blue-dark': !isActive
                  })}
                >
                  {freespins.amount}
                </span>
                <FormattedTag
                  id="loyalty-program.level-card.freespin"
                  className={classNames('h4 d-block font-weight-semi-bold mt-0_75 letter-spacing-default', {
                    'text-white-70': isActive,
                    'text-blue-dark-75': !isActive
                  })}
                />
              </div>
            )}

            {R.gt(gift, 0) && (
              <div>
                <span className={classNames('gift-amount font-weight-bold text-blue-dark', {
                  'text-white': isActive,
                  'text-blue-dark': !isActive
                })}
                >
                  {currencySymbol}{gift}
                </span>
                <FormattedTag
                  id="loyalty-program.gift"
                  className={classNames('h4 d-block font-weight-semi-bold ml-0_25 mt-0_75 letter-spacing-default', {
                    'text-white-70': isActive,
                    'text-blue-dark-75': !isActive
                  })}
                />
              </div>
            )}
          </div>
        </div>

        {(isCardFirst || isActive) && (
          <div className={classNames(`loyalty-card-start d-flex justify-content-center align-items-center d-flex mt-1_5 card-order-${order}`,
            {
              'bg-green-gradient': isActive,
              'bg-white': !isActive,
            },
            alignClassName)}
          >
            <FormattedTag
              id={isCardFirst ? 'loyalty-program.card.start' : 'loyalty-program.card.active'}
              className={classNames('h4 letter-spacing-default ml-2_75 ml-md-2 mr-0_75', {
                'text-white': isActive,
                'text-blue-dark-75': !isActive,
              })}
            />
            <IconArrowDown className="d-md-none" color={isActive ? colors.white : colors.gray40} />
          </div>
        )}

        {!isCardLast && (
          <>
            <DottedLineXs className="d-sm-none d-flex" />
            <IconArrowBottomBoldWithLine
              className={classNames(
                'dotted-line-wrapper position-relative d-flex flex-column mt-lg-0_25',
                { 'mt-sm-2 mt-lg-0': isCardFirst },
                dottedLineWrapperClassName,
                alignClassName
              )}
              classNamesDottedLine={this.getClassNameDottedLine(order, isLevelAccomplished)}
              isActive={isCardFirst}
              icons={dottedLineIcon}
            />
          </>
        )}
      </>
    );
  }
}

export const LoyaltyCard = withModalActions(
  withUser(
    injectIntl(
      withGameActions(LoyaltyCardUI), [
        USER_FIELDS.IS_USER_LOGGED_IN,
        USER_FIELDS.IS_SIGN_UP_COMPLETED,
        USER_FIELDS.IS_USER_REGISTERED,
      ]
    )
  )
);
